import React, { useState, ChangeEventHandler } from "react";
import { DestinationType, Organization } from "../common/ObjectTypes";
import GlobalSearch from "../common/GlobalSearch";
import SelectList from "../common/SelectList";
import { ArrowDown, ArrowUp, Clear, FilterIcon } from "../common/ProjectIcons";
import { useTranslation } from 'react-i18next';
import { filterClasses, buttonClasses } from "../common/tailwindClassnames";

type DestinationFilterProps = {
    destinationUrls: string[],
    destinationTypes: DestinationType[],
    organizations: Organization[],
    filters: any[],
    globalSearchStr?: string,
    onChangeGlobalSearch: Function,
    onRemoveTag: any,
    onResetFilters: any,
    onChangeEndpoint?: ChangeEventHandler,
    onChangeState?: ChangeEventHandler,
    onChangeType: ChangeEventHandler,
    onChangeDestinationTypeSearchBar: ChangeEventHandler,
    loadingDestinationTypes: boolean,
    loadMoreDestinationTypes: boolean,
    onLoadMoreDestinationTypes: Function,
    onChangeOwner: ChangeEventHandler,
    loadingOrganizations: boolean,
    loadMoreOrganizations: boolean,
    onLoadMoreOrganizations: Function,
    onChangeOwnerSearchBar: ChangeEventHandler,
    onChangeDestinationUrlSearchBar: ChangeEventHandler,
    loadMoreDestinationUrls: boolean,
    onLoadMoreDestinationUrls: Function,
    showStatus: boolean,
    showUrlFilter: boolean,
    loadingDestinationUrls: boolean
};

const DestinationFilter = ({
    destinationUrls,
    destinationTypes,
    organizations,
    filters,
    globalSearchStr,
    onChangeGlobalSearch,
    onRemoveTag,
    onResetFilters,
    onChangeEndpoint,
    onChangeState,
    onChangeType,
    onChangeDestinationTypeSearchBar,
    loadingDestinationTypes,
    loadMoreDestinationTypes,
    onLoadMoreDestinationTypes,
    onChangeOwner,
    loadingOrganizations,
    loadMoreOrganizations,
    onLoadMoreOrganizations,
    onChangeOwnerSearchBar,
    onChangeDestinationUrlSearchBar,
    loadMoreDestinationUrls,
    onLoadMoreDestinationUrls,
    showStatus,
    showUrlFilter,
    loadingDestinationUrls
}: DestinationFilterProps) => {
    const { t } = useTranslation();
    const [stateFilterExpanded, setStateFilterExpanded] = useState(true);
    const toggleStateFilter = () => setStateFilterExpanded(!stateFilterExpanded)
    return (
        // FILTERS
        <div className={filterClasses.container}>
            <GlobalSearch
                onChangeGlobalSearch={onChangeGlobalSearch}
                savedString={globalSearchStr}
            />
            <div className={filterClasses.header}>
                <h3><FilterIcon /> {t("Filters")}</h3>
                {filters.length > 0 && <h3 className={filterClasses.tagCount}>{filters.length}</h3>}
            </div>
            {/* tags of all of the filters */}
            {filters.length > 0 &&
                <div className="flex flex-col mt-3 pt-3">
                    {filters.map((item: any) => {
                        return (
                            <p className={filterClasses.tag}
                                key={item.id}
                            >
                                {t(item.name)}
                                <span className="pb-3 text-dark-green dark:text-dark-green-100 cursor-pointer"
                                    onClick={() => onRemoveTag(item.id)}>
                                    <Clear />
                                </span>
                            </p>
                        )
                    })}
                    <button className={buttonClasses.resetFilter}
                        onClick={onResetFilters}
                    >{t("Clear All")}</button>
                </div>}
            {/* destination url */}
            {showUrlFilter && <div className={filterClasses.section}>
                <SelectList
                    optionList={destinationUrls.map((url) => {
                        let new_url: string | URL;
                        try {
                            new_url = new URL(url)
                        } catch (err) {
                            new_url = ""
                        }
                        return {
                            name: new_url instanceof URL ? new_url.hostname : "",
                            id: url,
                            attribute_name: "base_url__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeEndpoint}
                    onChangeSearchBar={onChangeDestinationUrlSearchBar}
                    sectionLabel={t("Filter by Destination")}
                    loadMore={loadMoreDestinationUrls}
                    onClickLoadMore={() => onLoadMoreDestinationUrls()}
                    loading={loadingDestinationUrls}
                />
            </div>}
            {/* status */}
            {showStatus && <div className={filterClasses.section}>
                <div className="w-full flex flex-row justify-between cursor-pointer" onClick={toggleStateFilter}>
                    <label className="font-semibold text-off-black dark:text-white transition-all duration-300">{t("Filter by State")}</label>
                    {stateFilterExpanded ? <ArrowUp /> : <ArrowDown />}
                </div>
                {stateFilterExpanded &&
                    <div>
                        <div className="mt-1">
                            <input
                                className="accent-dark-green cursor-pointer"
                                checked={filters.map((a: any) => a.id).includes("true")}
                                type="checkbox"
                                value="active"
                                id="enabled"
                                onChange={onChangeState} />
                            <label
                                className="inline-block mx-2 text-off-black dark:text-white transition-all duration-300"
                            >
                                {t("Active")}
                            </label>
                        </div>
                        <div>
                            <input
                                className="accent-dark-green cursor-pointer"
                                checked={filters.map((a: any) => a.id).includes("false")}
                                type="checkbox"
                                value="inactive"
                                id="enabled"
                                onChange={onChangeState} />
                            <label
                                className="inline-block mx-2 text-off-black dark:text-white transition-all duration-300"
                            >
                                {t("Inactive")}
                            </label>
                        </div>
                    </div>
                }
            </div>}

            {/* type (uuid of related type) */}
            <div className={filterClasses.section}>
                <SelectList
                    optionList={destinationTypes.map((dest) => {
                        return {
                            name: dest.name,
                            id: dest.id,
                            attribute_name: "type__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeType}
                    sectionLabel={t("Filter by Technology")}
                    onChangeSearchBar={onChangeDestinationTypeSearchBar}
                    loadMore={loadMoreDestinationTypes}
                    onClickLoadMore={() => onLoadMoreDestinationTypes()}
                    loading={loadingDestinationTypes}
                />
            </div>
            {/* owner (uuid of related organization) */}
            <div className={filterClasses.section}>
                <SelectList
                    optionList={organizations.map((org) => {
                        return {
                            name: org.name,
                            id: org.id,
                            attribute_name: "owner__in"
                        }
                    })}
                    selectedList={filters}
                    onChangeFilter={onChangeOwner}
                    sectionLabel={t("Filter by Workspace")}
                    loadMore={loadMoreOrganizations}
                    onChangeSearchBar={onChangeOwnerSearchBar}
                    onClickLoadMore={() => onLoadMoreOrganizations()}
                    loading={loadingOrganizations}
                />
            </div>
        </div>
    )
}

export default DestinationFilter;